import { useState } from 'react'
import { Box, Flex, Text, Tabs, Button, Input, Badge, Card, VStack, HStack } from '@chakra-ui/react'
import { Plus, Search, FileText, CreditCard, Truck } from 'lucide-react'
import { useAccountingContext } from '../../context/AccountingContext'
import {
  FiscalYearSelector,
  SupplierModal,
  ContractModal,
  SupplierInvoiceModal,
  SupplierPaymentModal,
  SuppliersList,
  ContractsList,
  SupplierInvoicesList,
  SupplierPaymentsList,
  SupplierDetailDrawer,
  ContractDetailDrawer,
  SupplierInvoiceDetailDrawer,
  SupplierPaymentDetailDrawer,
} from '../../components'
import { useSuppliers, useContracts, useSupplierInvoices, useSupplierPayments } from '../../hooks'
import { useAuth } from '@/features/auth/hooks/useAuth'
import type {
  SupplierWithCategory,
  ContractWithSupplier,
  SupplierInvoiceWithRelations,
  SupplierPaymentWithSupplier,
} from '../../types'

type SubTab = 'suppliers' | 'contracts' | 'invoices' | 'payments'

export function SuppliersTab() {
  const { residenceId, activeFiscalYear, setSelectedFiscalYear } = useAccountingContext()
  const { data: user } = useAuth()
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('suppliers')
  const [searchTerm, setSearchTerm] = useState('')

  // Modal states
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false)
  const [isContractModalOpen, setIsContractModalOpen] = useState(false)
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)

  // Edit states
  const [supplierToEdit, setSupplierToEdit] = useState<SupplierWithCategory | null>(null)
  const [contractToEdit, setContractToEdit] = useState<ContractWithSupplier | null>(null)
  const [preSelectedSupplierId, setPreSelectedSupplierId] = useState<string | undefined>()

  // Drawer states - selected IDs for detail views
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null)
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null)
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)

  // Récupérer l'agency_id depuis le profile user
  const agencyId = user?.profile?.agency_id

  // Requêtes pour les statistiques et les données
  const { data: suppliers, isLoading: suppliersLoading } = useSuppliers({
    agency_id: agencyId || '',
    is_active: true,
  })

  const { data: contracts, isLoading: contractsLoading } = useContracts({
    residence_id: residenceId,
  })

  const { data: invoices, isLoading: invoicesLoading } = useSupplierInvoices({
    residence_id: residenceId,
    fiscal_year_id: activeFiscalYear?.id,
  })

  const { data: payments, isLoading: paymentsLoading } = useSupplierPayments({
    residence_id: residenceId,
    fiscal_year_id: activeFiscalYear?.id,
  })

  // Statistiques
  const activeContracts = contracts?.filter((c) => c.status === 'ACTIVE').length || 0
  const unpaidInvoices =
    invoices?.filter((i) => i.payment_status !== 'PAID' && i.status === 'VALIDATED').length || 0
  const pendingPayments = payments?.filter((p) => p.status === 'PENDING').length || 0

  // Handlers for add buttons
  const handleAddClick = () => {
    switch (activeSubTab) {
      case 'suppliers':
        setSupplierToEdit(null)
        setIsSupplierModalOpen(true)
        break
      case 'contracts':
        setContractToEdit(null)
        setIsContractModalOpen(true)
        break
      case 'invoices':
        setIsInvoiceModalOpen(true)
        break
      case 'payments':
        setPreSelectedSupplierId(undefined)
        setIsPaymentModalOpen(true)
        break
    }
  }

  const getAddButtonLabel = () => {
    switch (activeSubTab) {
      case 'suppliers':
        return 'Nouveau fournisseur'
      case 'contracts':
        return 'Nouveau contrat'
      case 'invoices':
        return 'Nouvelle facture'
      case 'payments':
        return 'Nouveau règlement'
    }
  }

  // Handlers for editing
  const handleEditSupplier = (supplier: SupplierWithCategory) => {
    setSupplierToEdit(supplier)
    setIsSupplierModalOpen(true)
  }

  const handleEditContract = (contract: ContractWithSupplier) => {
    setContractToEdit(contract)
    setIsContractModalOpen(true)
  }

  // Handler pour créer un règlement depuis une facture
  const handleCreatePaymentFromInvoice = (invoice: SupplierInvoiceWithRelations) => {
    setPreSelectedSupplierId(invoice.supplier_id)
    setIsPaymentModalOpen(true)
  }

  // Handlers for viewing - open detail drawers
  const handleViewSupplier = (supplier: SupplierWithCategory) => {
    setSelectedSupplierId(supplier.id)
  }

  const handleViewContract = (contract: ContractWithSupplier) => {
    setSelectedContractId(contract.id)
  }

  const handleViewInvoice = (invoice: SupplierInvoiceWithRelations) => {
    setSelectedInvoiceId(invoice.id)
  }

  const handleViewPayment = (payment: SupplierPaymentWithSupplier) => {
    setSelectedPaymentId(payment.id)
  }

  const handleEditInvoice = (invoice: SupplierInvoiceWithRelations) => {
    // TODO: Open invoice edit modal (only for drafts)
    console.log('Edit invoice:', invoice.id)
  }

  // Drawer navigation helpers
  const getSupplierIndex = () => suppliers?.findIndex((s) => s.id === selectedSupplierId) ?? -1
  const getContractIndex = () => contracts?.findIndex((c) => c.id === selectedContractId) ?? -1
  const getInvoiceIndex = () => invoices?.findIndex((i) => i.id === selectedInvoiceId) ?? -1
  const getPaymentIndex = () => payments?.findIndex((p) => p.id === selectedPaymentId) ?? -1

  return (
    <Box>
      {/* Header avec sélecteur d'exercice */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="lg" fontWeight="semibold">
            Gestion des Fournisseurs
          </Text>
          <Text fontSize="sm" color="gray.500">
            Fournisseurs, contrats, factures et règlements
          </Text>
        </Box>

        <FiscalYearSelector
          residenceId={residenceId || ''}
          value={activeFiscalYear?.id || ''}
          onChange={(fy) => setSelectedFiscalYear(fy)}
        />
      </Flex>

      {/* Statistiques rapides */}
      <Flex gap={4} mb={6}>
        <Card.Root flex={1} variant="outline">
          <Card.Body p={4}>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Fournisseurs actifs
              </Text>
              <HStack>
                <Truck size={20} color="var(--chakra-colors-brand-500)" />
                <Text fontSize="2xl" fontWeight="bold">
                  {suppliers?.length || 0}
                </Text>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        <Card.Root flex={1} variant="outline">
          <Card.Body p={4}>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Contrats actifs
              </Text>
              <HStack>
                <FileText size={20} color="var(--chakra-colors-green-500)" />
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {activeContracts}
                </Text>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        <Card.Root flex={1} variant="outline">
          <Card.Body p={4}>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Factures impayées
              </Text>
              <HStack>
                <FileText size={20} color="var(--chakra-colors-orange-500)" />
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color={unpaidInvoices > 0 ? 'orange.600' : 'gray.600'}
                >
                  {unpaidInvoices}
                </Text>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        <Card.Root flex={1} variant="outline">
          <Card.Body p={4}>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Règlements en attente
              </Text>
              <HStack>
                <CreditCard size={20} color="var(--chakra-colors-blue-500)" />
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color={pendingPayments > 0 ? 'blue.600' : 'gray.600'}
                >
                  {pendingPayments}
                </Text>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Flex>

      {/* Sous-navigation par onglets avec CTA */}
      <Tabs.Root
        value={activeSubTab}
        onValueChange={(details) => {
          setActiveSubTab(details.value as SubTab)
          setSearchTerm('')
        }}
        variant="enclosed"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Tabs.List>
            <Tabs.Trigger value="suppliers">
              <Truck size={16} />
              <Text ml={2}>Fournisseurs</Text>
              <Badge ml={2} colorPalette="gray" size="sm">
                {suppliers?.length || 0}
              </Badge>
            </Tabs.Trigger>
            <Tabs.Trigger value="contracts">
              <FileText size={16} />
              <Text ml={2}>Contrats</Text>
              <Badge ml={2} colorPalette="green" size="sm">
                {activeContracts}
              </Badge>
            </Tabs.Trigger>
            <Tabs.Trigger value="invoices">
              <FileText size={16} />
              <Text ml={2}>Factures</Text>
              {unpaidInvoices > 0 && (
                <Badge ml={2} colorPalette="orange" size="sm">
                  {unpaidInvoices}
                </Badge>
              )}
            </Tabs.Trigger>
            <Tabs.Trigger value="payments">
              <CreditCard size={16} />
              <Text ml={2}>Règlements</Text>
              {pendingPayments > 0 && (
                <Badge ml={2} colorPalette="blue" size="sm">
                  {pendingPayments}
                </Badge>
              )}
            </Tabs.Trigger>
          </Tabs.List>

          <Button colorPalette="brand" onClick={handleAddClick}>
            <Plus size={16} />
            {getAddButtonLabel()}
          </Button>
        </Flex>

        {/* Barre de recherche */}
        <Flex mb={4}>
          <Box position="relative" flex={1} maxW="400px">
            <Box
              position="absolute"
              left={3}
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.400"
            >
              <Search size={16} />
            </Box>
            <Input
              pl={10}
              placeholder={
                activeSubTab === 'suppliers'
                  ? 'Rechercher un fournisseur...'
                  : activeSubTab === 'contracts'
                    ? 'Rechercher un contrat...'
                    : activeSubTab === 'invoices'
                      ? 'Rechercher une facture...'
                      : 'Rechercher un règlement...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
        </Flex>

        {/* Contenu des onglets */}
        <Tabs.Content value="suppliers">
          <SuppliersList
            suppliers={suppliers}
            searchTerm={searchTerm}
            onEdit={handleEditSupplier}
            onView={handleViewSupplier}
            isLoading={suppliersLoading}
          />
        </Tabs.Content>

        <Tabs.Content value="contracts">
          <ContractsList
            contracts={contracts}
            searchTerm={searchTerm}
            onEdit={handleEditContract}
            onView={handleViewContract}
            isLoading={contractsLoading}
          />
        </Tabs.Content>

        <Tabs.Content value="invoices">
          <SupplierInvoicesList
            invoices={invoices}
            searchTerm={searchTerm}
            onEdit={handleEditInvoice}
            onView={handleViewInvoice}
            onCreatePayment={handleCreatePaymentFromInvoice}
            isLoading={invoicesLoading}
          />
        </Tabs.Content>

        <Tabs.Content value="payments">
          <SupplierPaymentsList
            payments={payments}
            searchTerm={searchTerm}
            onView={handleViewPayment}
            isLoading={paymentsLoading}
          />
        </Tabs.Content>
      </Tabs.Root>

      {/* Modales */}
      {agencyId && (
        <SupplierModal
          isOpen={isSupplierModalOpen}
          onClose={() => {
            setIsSupplierModalOpen(false)
            setSupplierToEdit(null)
          }}
          agencyId={agencyId}
          supplier={supplierToEdit}
        />
      )}

      <ContractModal
        isOpen={isContractModalOpen}
        onClose={() => {
          setIsContractModalOpen(false)
          setContractToEdit(null)
        }}
        residenceId={residenceId}
        fiscalYearId={activeFiscalYear?.id}
        contract={contractToEdit}
      />

      {activeFiscalYear && (
        <SupplierInvoiceModal
          isOpen={isInvoiceModalOpen}
          onClose={() => setIsInvoiceModalOpen(false)}
          residenceId={residenceId}
          fiscalYearId={activeFiscalYear.id}
        />
      )}

      {activeFiscalYear && (
        <SupplierPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false)
            setPreSelectedSupplierId(undefined)
          }}
          residenceId={residenceId}
          fiscalYearId={activeFiscalYear.id}
          preSelectedSupplierId={preSelectedSupplierId}
        />
      )}

      {/* Detail Drawers */}
      <SupplierDetailDrawer
        supplierId={selectedSupplierId}
        isOpen={!!selectedSupplierId}
        onClose={() => setSelectedSupplierId(null)}
        onNavigatePrevious={() => {
          const idx = getSupplierIndex()
          const prev = suppliers?.[idx - 1]
          if (idx > 0 && prev) setSelectedSupplierId(prev.id)
        }}
        onNavigateNext={() => {
          const idx = getSupplierIndex()
          const next = suppliers?.[idx + 1]
          if (next) setSelectedSupplierId(next.id)
        }}
        hasPrevious={getSupplierIndex() > 0}
        hasNext={!!suppliers && getSupplierIndex() < suppliers.length - 1}
      />

      <ContractDetailDrawer
        contractId={selectedContractId}
        isOpen={!!selectedContractId}
        onClose={() => setSelectedContractId(null)}
        onNavigatePrevious={() => {
          const idx = getContractIndex()
          const prev = contracts?.[idx - 1]
          if (idx > 0 && prev) setSelectedContractId(prev.id)
        }}
        onNavigateNext={() => {
          const idx = getContractIndex()
          const next = contracts?.[idx + 1]
          if (next) setSelectedContractId(next.id)
        }}
        hasPrevious={getContractIndex() > 0}
        hasNext={!!contracts && getContractIndex() < contracts.length - 1}
      />

      <SupplierInvoiceDetailDrawer
        invoiceId={selectedInvoiceId}
        isOpen={!!selectedInvoiceId}
        onClose={() => setSelectedInvoiceId(null)}
        onNavigatePrevious={() => {
          const idx = getInvoiceIndex()
          const prev = invoices?.[idx - 1]
          if (idx > 0 && prev) setSelectedInvoiceId(prev.id)
        }}
        onNavigateNext={() => {
          const idx = getInvoiceIndex()
          const next = invoices?.[idx + 1]
          if (next) setSelectedInvoiceId(next.id)
        }}
        hasPrevious={getInvoiceIndex() > 0}
        hasNext={!!invoices && getInvoiceIndex() < invoices.length - 1}
      />

      <SupplierPaymentDetailDrawer
        paymentId={selectedPaymentId}
        isOpen={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        onNavigatePrevious={() => {
          const idx = getPaymentIndex()
          const prev = payments?.[idx - 1]
          if (idx > 0 && prev) setSelectedPaymentId(prev.id)
        }}
        onNavigateNext={() => {
          const idx = getPaymentIndex()
          const next = payments?.[idx + 1]
          if (next) setSelectedPaymentId(next.id)
        }}
        hasPrevious={getPaymentIndex() > 0}
        hasNext={!!payments && getPaymentIndex() < payments.length - 1}
      />
    </Box>
  )
}
